import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useAppData } from '../context/AppDataContext';
import { getUpstashSensorDataCache } from '../utils/upstashRedisCache';
import { AggregatedDataService } from '../services/AggregatedDataService';

const toCleanMac = (mac) => (mac ? mac.replace(/[^0-9A-Fa-f]/g, '').toUpperCase() : '');
const DATA_WINDOW_MINUTES = 10;

export { DATA_WINDOW_MINUTES };

export const useRedisGatewayDetail = (gatewayId) => {
  const [gateway, setGateway] = useState(null);
  const [gatewayHealth, setGatewayHealth] = useState(null);
  const [connectedSensors, setConnectedSensors] = useState([]);
  const [optimisticConnectedSensors, setOptimisticConnectedSensors] = useState([]); // For immediate UI updates
  // allUserSensors comes from the global context now — no independent fetch.
  const [gatewaySensorReadings, setGatewaySensorReadings] = useState({}); // Map of sensor_id -> latest reading
  const [gatewayDataStatus, setGatewayDataStatus] = useState({
    isConnected: false,
    lastDataReceived: null,
    dataCount: 0,
  });
  const [gatewayConnectionSource, setGatewayConnectionSource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const { sensors: allUserSensors } = useAppData();
  const healthLoadingRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  const hasInitialLoad = useRef(false);
  
  const cache = getUpstashSensorDataCache();

  // Load gateway data from cache or database.
  // Stale-while-revalidate: if we have cached data we show it immediately and
  // never show a blocking spinner on subsequent loads — only on the very first.
  const loadGatewayData = useCallback(async () => {
    if (!gatewayId || !isAuthenticated) return;

    const cacheKey = `gateway:${gatewayId}`;

    // Only block the UI with a spinner on the very first load.
    const shouldBlock = !hasInitialLoad.current;
    if (shouldBlock) {
      setIsLoading(true);
    }
    if (!shouldBlock) setError(null);

    try {
      // Try to get gateway data from cache first
      const cachedGateway = await cache.redisRequest(['GET', cacheKey]);

      if (cachedGateway) {
        const gatewayData = JSON.parse(cachedGateway);
        setGateway(gatewayData);

        // Show cached data immediately (stale-while-revalidate).
        setGatewayHealth(gatewayData.health || null);
        setConnectedSensors(gatewayData.connected_sensors || []);
        setGatewaySensorReadings(gatewayData.sensor_readings || {});
        setGatewayDataStatus(gatewayData.data_status || {
          isConnected: false,
          lastDataReceived: null,
          dataCount: 0,
        });
        setGatewayConnectionSource(gatewayData.connection_source || null);
        hasInitialLoad.current = true;
        setIsLoading(false);

        // If cache is fresh (< 5 min), we're done — no need to hit the DB.
        const cacheAge = Date.now() - new Date(gatewayData.cached_at).getTime();
        if (cacheAge < 300000) return;
        // Cache stale → fall through to background refresh (no spinner).
      }

      // Fetch the gateway record (sensors come from the global context now).
      const { data: gatewayData } = await supabase
        .from('gateways')
        .select('*')
        .eq('id', gatewayId)
        .single();

      if (gatewayData) {
        setGateway(gatewayData);

        // Cache the gateway record
        const cacheData = {
          ...gatewayData,
          cached_at: new Date().toISOString(),
          health: null,
          connected_sensors: [],
          sensor_readings: {},
          data_status: {
            isConnected: false,
            lastDataReceived: null,
            dataCount: 0,
          },
          connection_source: null
        };
        await cache.redisRequest(['SETEX', cacheKey, '300', JSON.stringify(cacheData)]);
      }

      // Load all three detail datasets IN PARALLEL (was: sequential waterfall).
      await Promise.allSettled([
        loadGatewayHealth(gatewayData),
        loadConnectedSensors(gatewayData),
        checkGatewayDataStatus(gatewayData),
      ]);

      hasInitialLoad.current = true;

    } catch (error) {
      console.error('Error loading gateway data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
    // Functions loadGatewayHealth, loadConnectedSensors, checkGatewayDataStatus are called directly
    // but cannot be added as deps due to initialization order - they're stable callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId, isAuthenticated, user?.id, cache]);

  // Load gateway health data (same as React Native)
  const loadGatewayHealth = useCallback(async (gatewayData) => {
    if (!gatewayData) return;

    try {
      const cleanMac = toCleanMac(gatewayData.mac_address);
      if (!cleanMac || !user?.id) return;

      // Fetch latest rows and find the newest 'alive' message client-side (same as React Native)
      const { data: rows, error } = await supabase
        .from('user_raw_gateway_data')
        .select('raw_payload, received_at')
        .eq('gateway_mac', cleanMac)
        .eq('user_id', user.id)
        .order('received_at', { ascending: false })
        .limit(50);

      if (error) {
        console.warn('Gateway health fetch error:', error);
        setGatewayHealth(null);
        return;
      }
      
      if (!rows || rows.length === 0) {
        setGatewayHealth(null);
        return;
      }

      // Find the latest and previous 'alive' messages (same as React Native)
      let latestHeartbeat = null;
      let previousHeartbeat = null;

      for (const r of rows) {
        let payload = r.raw_payload;
        if (payload && typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch { payload = null; }
        }
        if (payload && payload.msg === 'alive') {
          if (!latestHeartbeat) {
            latestHeartbeat = { row: r, payload };
          } else if (!previousHeartbeat) {
            previousHeartbeat = { row: r, payload };
            break; // Found both, can stop
          }
        }
      }

      if (!latestHeartbeat) {
        setGatewayHealth(null);
        return;
      }

      const heartbeatAt = latestHeartbeat.row.received_at;
      const previousHeartbeatAt = previousHeartbeat?.row?.received_at;
      const payload = latestHeartbeat.payload;

      // Calculate interval between heartbeats if we have both (same as React Native)
      let intervalSeconds;
      if (previousHeartbeatAt) {
        const latestTime = new Date(heartbeatAt).getTime();
        const previousTime = new Date(previousHeartbeatAt).getTime();
        intervalSeconds = Math.floor((latestTime - previousTime) / 1000);
      }

      const healthData = {
        heartbeatAt,
        previousHeartbeatAt,
        intervalSeconds,
        firmware: payload.ver,
        blever: payload.blever,
        wanIP: payload.wanIP,
        uptime: payload.uptime,
        load: payload.load,
        temp: payload.temp,
        mem_free: payload.mem_free,
        model: payload.model,
        latitude: payload.location?.latitude,
        longitude: payload.location?.longitude,
        altitude: payload.location?.altitude,
      };

      setGatewayHealth(healthData);
      
      // Cache the health data
      const healthCacheKey = `gateway_health:${cleanMac}`;
      await cache.redisRequest(['SETEX', healthCacheKey, '600', JSON.stringify([healthData])]);
      
      // Update cached gateway data with health
      const gatewayCacheKey = `gateway:${gatewayId}`;
      const cachedGateway = await cache.redisRequest(['GET', gatewayCacheKey]);
      if (cachedGateway) {
        const gatewayData = JSON.parse(cachedGateway);
        gatewayData.health = healthData;
        await cache.redisRequest(['SETEX', gatewayCacheKey, '300', JSON.stringify(gatewayData)]);
      }
      
    } catch (error) {
      console.error('Error loading gateway health:', error);
      setGatewayHealth(null);
    }
  }, [gatewayId, user, cache]);

  // Load connected sensors
  const loadConnectedSensors = useCallback(async (gatewayData) => {
    if (!gatewayData) return;

    try {
      // Skip sensor_gateways table since it doesn't exist - use sensors assigned to gateway
      const { data: connectedSensorData } = await supabase
        .from('sensors')
        .select('*')
        .eq('gateway_id', gatewayId)
        .eq('user_id', user.id);

      setConnectedSensors(connectedSensorData || []);
      
      // Update cached gateway data with connected sensors
      const gatewayCacheKey = `gateway:${gatewayId}`;
      const cachedGateway = await cache.redisRequest(['GET', gatewayCacheKey]);
      if (cachedGateway) {
        const gatewayData = JSON.parse(cachedGateway);
        gatewayData.connected_sensors = connectedSensorData || [];
        await cache.redisRequest(['SETEX', gatewayCacheKey, '300', JSON.stringify(gatewayData)]);
      }
      
    } catch (error) {
      console.error('Error loading connected sensors:', error);
    }
  }, [gatewayId, user?.id, cache]); // Add dependencies

  // Check gateway data status with Redis caching
  const checkGatewayDataStatus = useCallback(async (gatewayData) => {
    if (!gatewayData) return;

    try {
      const cleanMac = toCleanMac(gatewayData.mac_address);
      if (!cleanMac) return;

      const sinceIso = new Date(Date.now() - DATA_WINDOW_MINUTES * 60 * 1000).toISOString();
      let latestTs = gatewayData.last_seen_at ? new Date(gatewayData.last_seen_at) : null;
      let combinedCount = 0;
      let connectionSource = null;

      // Try to get cached gateway data status
      const statusCacheKey = `gateway_status:${cleanMac}`;
      const cachedStatus = await cache.redisRequest(['GET', statusCacheKey]);
      
      if (cachedStatus) {
        const statusData = JSON.parse(cachedStatus);
        const cacheAge = Date.now() - new Date(statusData.cached_at).getTime();
        
        // Use cached status if less than 2 minutes old
        if (cacheAge < 120000) {
          setGatewayDataStatus(statusData.data_status);
          setGatewayConnectionSource(statusData.connection_source);
          return;
        }
      }

      // Fetch fresh data from user_raw_gateway_data (like React Native)
      const [{ data: latestRaw }, { count: countRaw }] = await Promise.all([
        supabase
          .from('user_raw_gateway_data')
          .select('received_at, source')
          .eq('gateway_mac', cleanMac)
          .eq('user_id', user.id)
          .order('received_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('user_raw_gateway_data')
          .select('id', { count: 'exact' })
          .eq('gateway_mac', cleanMac)
          .eq('user_id', user.id)
          .gte('received_at', sinceIso)
      ]);

      if (latestRaw) {
        const rawTs = new Date(latestRaw.received_at);
        if (!latestTs || rawTs > latestTs) {
          latestTs = rawTs;
          connectionSource = latestRaw.source;
        }
      }

      combinedCount = countRaw || 0;

      // Also check sensor_readings table for this gateway's sensors
      if (connectedSensors.length > 0) {
        // Fix: use s.id not s.sensor_id
        const connectedSensorIds = connectedSensors.map(s => s.id).filter(id => id !== undefined);
        if (connectedSensorIds.length > 0) {
          const { count: sensorCount } = await supabase
            .from('sensor_readings')
            .select('id', { count: 'exact' })
            .in('sensor_id', connectedSensorIds)
            .gte('created_at', sinceIso);

          combinedCount += sensorCount || 0;
        }
      }

      const now = new Date();
      const cutoff = new Date(now.getTime() - DATA_WINDOW_MINUTES * 60 * 1000);
      const isConnected = latestTs ? latestTs > cutoff : false;

      const newStatus = {
        isConnected,
        lastDataReceived: latestTs ? latestTs.toISOString() : null,
        dataCount: combinedCount,
      };

      setGatewayDataStatus(newStatus);
      setGatewayConnectionSource(connectionSource);

      // Cache the status
      const statusData = {
        data_status: newStatus,
        connection_source: connectionSource,
        cached_at: new Date().toISOString()
      };
      await cache.redisRequest(['SETEX', statusCacheKey, '120', JSON.stringify(statusData)]); // 2 minutes TTL
      
      // Update cached gateway data with status
      const gatewayCacheKey = `gateway:${gatewayId}`;
      const cachedGateway = await cache.redisRequest(['GET', gatewayCacheKey]);
      if (cachedGateway) {
        const gatewayData = JSON.parse(cachedGateway);
        gatewayData.data_status = newStatus;
        gatewayData.connection_source = connectionSource;
        await cache.redisRequest(['SETEX', gatewayCacheKey, '300', JSON.stringify(gatewayData)]);
      }
      
    } catch (error) {
      console.error('Error checking gateway data status:', error);
    }
    // Using connectedSensors.length instead of full array to prevent re-renders on reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId, user?.id, connectedSensors.length]);

  // Load latest readings for connected sensors from gateway_sensor_readings table
  const loadSensorReadings = useCallback(async () => {
    if (connectedSensors.length === 0 || !gateway?.id) return;

    try {
      // Fix: use s.id (not s.sensor_id) since connectedSensors come from sensors table
      const sensorIds = connectedSensors.map(s => s.id).filter(id => id !== undefined);
      
      if (sensorIds.length === 0) return;

      // Fetch from gateway_sensor_readings table (primary source for gateway data)
      const { data: gatewayReadingsData, error: gatewayError } = await supabase
        .from('gateway_sensor_readings')
        .select('sensor_id, timestamp, temperature, humidity, battery_voltage, device_type, source, registration_status, processed_at')
        .eq('gateway_id', gateway.id)
        .in('sensor_id', sensorIds)
        .order('timestamp', { ascending: false })
        .limit(1000); // Get recent readings to filter client-side

      if (gatewayError) {
        console.warn('Error fetching gateway_sensor_readings:', gatewayError);
      }

      // Group by sensor_id and get latest for each
      const readingsMap = {};
      if (gatewayReadingsData) {
        gatewayReadingsData.forEach(reading => {
          if (reading.sensor_id) {
            if (!readingsMap[reading.sensor_id] || 
                new Date(reading.timestamp) > new Date(readingsMap[reading.sensor_id].timestamp)) {
              readingsMap[reading.sensor_id] = reading;
            }
          }
        });
      }

      // Fallback to sensor_readings for sensors without gateway_sensor_readings data
      const missingSensorIds = sensorIds.filter(id => !readingsMap[id]);
      
      if (missingSensorIds.length > 0) {
        const { data: sensorReadingsData, error: sensorError } = await supabase
          .from('sensor_readings')
          .select('sensor_id, recorded_at, temperature, humidity, battery_level, source, created_at')
          .in('sensor_id', missingSensorIds)
          .in('source', ['mqtt', 'http', 'app']) // Gateway and app sources
          .order('recorded_at', { ascending: false })
          .limit(1000);

        if (sensorError) {
          console.warn('Error fetching sensor_readings fallback:', sensorError);
        }

        // Group by sensor_id for fallback readings
        if (sensorReadingsData) {
          sensorReadingsData.forEach(reading => {
            if (reading.sensor_id) {
              if (!readingsMap[reading.sensor_id] || 
                  new Date(reading.recorded_at) > new Date(readingsMap[reading.sensor_id].timestamp || 0)) {
                // Map sensor_readings format to gateway_sensor_readings format
                readingsMap[reading.sensor_id] = {
                  sensor_id: reading.sensor_id,
                  timestamp: reading.recorded_at,
                  temperature: reading.temperature,
                  humidity: reading.humidity,
                  battery_voltage: reading.battery_level,
                  source: reading.source,
                  processed_at: reading.created_at,
                  registration_status: null // sensor_readings doesn't have this
                };
              }
            }
          });
        }
      }

      setGatewaySensorReadings(readingsMap);

    } catch (error) {
      console.error('Error loading sensor readings:', error);
    }
    // Using connectedSensors.length instead of full array to prevent re-renders
    // cache is stable and doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedSensors.length, gateway?.id]);

  // Toggle sensor connection to gateway
  const handleSensorToggle = useCallback(async (sensorId, isActive) => {
    if (!gatewayId || !user?.id) return;

    setIsToggling(true);
    try {
      // Optimistic update: immediately update the optimistic state only
      if (isActive) {
        // Adding sensor to gateway - add to optimisticConnectedSensors if not already there
        setOptimisticConnectedSensors(prev => {
          const sensorToAdd = allUserSensors.find(s => s.id === sensorId);
          if (sensorToAdd && !prev.find(s => s.id === sensorId)) {
            return [...prev, { ...sensorToAdd, gateway_id: gatewayId }];
          }
          return prev;
        });
      } else {
        // Removing sensor from gateway - remove from optimisticConnectedSensors
        setOptimisticConnectedSensors(prev => prev.filter(s => s.id !== sensorId));
      }

      // Update the sensors table directly (no sensor_gateways table exists)
      // If isActive=true, assign sensor to this gateway
      // If isActive=false, unassign sensor from this gateway
      const { error } = await supabase
        .from('sensors')
        .update({
          gateway_id: isActive ? gatewayId : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', sensorId)
        .eq('user_id', user.id); // Ensure user owns this sensor

      if (error) throw error;

      // Refresh connected sensors to ensure consistency with server
      await loadConnectedSensors(gateway);
      
      // Clear relevant cache entries
      await cache.clearSensorCache(sensorId);
      const gatewayCacheKey = `gateway:${gatewayId}`;
      await cache.redisRequest(['DEL', gatewayCacheKey]);
      
      // Sync optimistic state with real state after successful update
      setOptimisticConnectedSensors([]);
      
    } catch (error) {
      console.error('Error toggling sensor:', error);
      setError(error.message);
      
      // Revert optimistic update on error
      setOptimisticConnectedSensors([]);
      await loadConnectedSensors(gateway);
    } finally {
      setIsToggling(false);
    }
  }, [gatewayId, gateway, user?.id, cache, loadConnectedSensors, allUserSensors]);

  // Initialize and set up real-time updates
  useEffect(() => {
    if (!gatewayId || !isAuthenticated) return;

    loadGatewayData();

    // Don't set up subscriptions until we have gateway data to avoid excessive re-renders
    if (!gateway) return;

    // Set up real-time subscriptions with unique channel IDs to prevent conflicts
    const subscriptions = [];
    const channelId = `gateway_${gatewayId}_${Date.now()}`;

    // Listen for raw gateway data updates (same as React Native) - this handles both data and health
    const rawDataSubscription = supabase
      .channel(`${channelId}_urgd_${toCleanMac(gateway?.mac_address)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_raw_gateway_data',
          filter: `gateway_mac=eq.${toCleanMac(gateway?.mac_address)}`
        },
        async (payload) => {
          try {
            const receivedAt = payload?.new?.received_at ? new Date(payload.new.received_at) : new Date();
            
            // Optimistically update gateway data status
            setGatewayDataStatus(prev => ({
              isConnected: true,
              lastDataReceived: receivedAt.toISOString(),
              dataCount: (prev?.dataCount || 0) + 1
            }));
            
            // Update gateway last_seen_at in DB (same as React Native)
            await supabase
              .from('gateways')
              .update({ 
                last_seen_at: receivedAt.toISOString(), 
                last_seen: receivedAt.toISOString(), 
                updated_at: new Date().toISOString() 
              })
              .eq('id', gatewayId);
              
            // Check if this is an 'alive' message and update health
            let rawPayload = payload.new.raw_payload;
            if (rawPayload && typeof rawPayload === 'string') {
              try { rawPayload = JSON.parse(rawPayload); } catch { rawPayload = null; }
            }
            
            if (rawPayload && rawPayload.msg === 'alive') {
              await loadGatewayHealth(gateway);
            }
            
          } catch (e) {
            console.warn('Realtime update error:', e);
          }
        }
      )
      .subscribe();
    
    subscriptions.push(rawDataSubscription);

    // Listen for sensor reading updates - but ONLY for this gateway's sensors
    // Fix: check s.id not s.sensor_id
    const connectedSensorIds = connectedSensors.map(s => s.id).filter(id => id !== undefined);
    
    if (connectedSensorIds.length > 0) {
      const readingsSubscription = supabase
        .channel(`${channelId}_sensor_readings`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'sensor_readings',
            filter: `sensor_id=in.(${connectedSensorIds.join(',')})`
          },
          async (payload) => {
            const sensorId = payload.new.sensor_id;
            if (connectedSensorIds.includes(sensorId)) {
              // Debounce the updates to prevent flickering
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              
              debounceTimerRef.current = setTimeout(async () => {
                await loadSensorReadings();
                await checkGatewayDataStatus(gateway);
              }, 500); // 500ms debounce
            }
          }
        )
        .subscribe();
      
      subscriptions.push(readingsSubscription);
    }

    return () => {
      subscriptions.forEach(sub => supabase.removeChannel(sub));
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // Only depend on stable primitives to prevent re-subscription flickering
    // Functions are stable callbacks and don't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId, isAuthenticated, gateway?.mac_address]);

  // Load sensor readings when connected sensors change (but debounce to prevent flickering)
  useEffect(() => {
    if (connectedSensors.length > 0) {
      // Debounce to prevent multiple rapid calls
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      debounceTimerRef.current = setTimeout(() => {
        loadSensorReadings();
      }, 300); // 300ms debounce
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [connectedSensors.length, loadSensorReadings]);

  // Merge connectedSensors with optimistic updates for UI
  const displayConnectedSensors = useMemo(() => {
    if (optimisticConnectedSensors.length === 0) {
      return connectedSensors;
    }
    
    // Create a map of current connected sensors
    const sensorMap = new Map(connectedSensors.map(s => [s.id, s]));
    
    // Apply optimistic updates
    optimisticConnectedSensors.forEach(optimisticSensor => {
      if (optimisticSensor.gateway_id === gatewayId) {
        sensorMap.set(optimisticSensor.id, optimisticSensor);
      } else {
        sensorMap.delete(optimisticSensor.id);
      }
    });
    
    return Array.from(sensorMap.values());
  }, [connectedSensors, optimisticConnectedSensors, gatewayId]);

  return {
    gateway,
    gatewayHealth,
    connectedSensors: displayConnectedSensors, // Use merged state for UI
    allUserSensors,
    gatewaySensorReadings,
    gatewayDataStatus,
    gatewayConnectionSource,
    isLoading,
    isToggling,
    error,
    handleSensorToggle,
    refresh: loadGatewayData
  };
};
